import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Fab,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  Grid,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import { motion } from 'framer-motion';
import { format, isValid, differenceInDays } from 'date-fns';
import { alpha } from '@mui/material/styles';
import { Fade } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { getItineraryById, toggleFavorite, deleteItinerary } from '../services/itineraryService';
import { getDestinationImage, DEFAULT_DESTINATION_IMAGE } from '../utils/imageUtils';
import { createShareLink } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';
import { printItinerary } from '../utils/printUtils';
import ItineraryPage from './ItineraryPage';

// Helper function to create a local date from a string or Date object
const createLocalDate = (dateStr) => {
  // If it's already a date, return it
  if (dateStr instanceof Date) return dateStr;
  
  // Handle ISO string or YYYY-MM-DD format
  if (typeof dateStr === 'string') {
    // If in YYYY-MM-DD format, parse it locally
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day); // month is 0-indexed in JS Date
    }
    // Otherwise use standard Date constructor
    return new Date(dateStr);
  }
  
  // Fallback
  return new Date();
};

// Helper function to safely format dates
const safeFormatDate = (date, formatString) => {
  if (!date) return 'N/A';
  
  const dateObj = createLocalDate(date);
  return isValid(dateObj) ? format(dateObj, formatString) : 'Invalid date';
};

const SavedItineraryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const printParam = searchParams.get('print');
  const walletParam = searchParams.get('wallet');
  
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [destinationImage, setDestinationImage] = useState(DEFAULT_DESTINATION_IMAGE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkExpiry, setShareLinkExpiry] = useState('7d');
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [shareSnackbarMessage, setShareSnackbarMessage] = useState('');
  
  useEffect(() => {
    const fetchItinerary = async () => {
      try {
        setLoading(true);
        const data = await getItineraryById(id);
        
        if (!data) {
          setError('Itinerary not found');
          setLoading(false);
          return;
        }
        
        // Process and normalize the raw itinerary data
        const normalizedItinerary = normalizeItineraryData(data);
        
        setItinerary(normalizedItinerary);
        
        // Set the destination image
        if (data.destination) {
          const imageUrl = getDestinationImage(data.destination);
          setDestinationImage(imageUrl);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching itinerary:', error);
        setError('Failed to load itinerary. Please try again.');
        setLoading(false);
      }
    };
    
    // Wait for authentication to complete
    if (!authLoading) {
      if (user) {
        fetchItinerary();
      } else {
        // Redirect to login if no user
        navigate('/login', { 
          state: { 
            from: `/itinerary/${id}`,
            message: 'Please login to view this itinerary' 
          } 
        });
      }
    }
  }, [user, authLoading, id, navigate]);

  // Function to normalize itinerary data structure
  const normalizeItineraryData = (data) => {
    if (!data) return null;
    
    console.log("Normalizing itinerary data:", data);
    
    // Create a copy to avoid mutating the original data
    const normalizedData = { ...data };
    
    // Extract travel info from various possible locations
    const travelInfo = data.itineraryData?.travelInfo || 
                       data.travelInfo || 
                       (data.itineraryData?.itineraryData?.travelInfo) || 
                       null;
    
    
    // If itineraryData property exists and contains the days (saved itinerary format)
    if (normalizedData.itineraryData) {
      
      // Create a proper formatted itinerary object that matches what ItineraryPage expects
      const result = {
        id: normalizedData.id,
        destination: normalizedData.destination,
        startDate: createLocalDate(normalizedData.startDate),
        endDate: createLocalDate(normalizedData.endDate),
        travelers: normalizedData.travelers,
        adults: normalizedData.adults || normalizedData.travelers,
        children: normalizedData.children || 0,
        budget: normalizedData.budget,
        interests: normalizedData.interests,
        isFavorite: normalizedData.isFavorite,
        summary: normalizedData.itineraryData.summary || "",
        days: (normalizedData.itineraryData.days || []).map(day => ({
          ...day,
          date: createLocalDate(day.date)
        })),
        tips: normalizedData.itineraryData.tips || [],
        travelInfo: travelInfo,
        // Add metadata
        originalId: id,
        isSaved: true
      };
      
      
      return result;
    }
    
    // In case the data is already in the right format
    const result = {
      ...normalizedData,
      startDate: createLocalDate(normalizedData.startDate),
      endDate: createLocalDate(normalizedData.endDate),
      days: (normalizedData.days || []).map(day => ({
        ...day,
        date: createLocalDate(day.date)
      })),
      travelInfo: travelInfo,
      originalId: id,
      isSaved: true
    };
    
    console.log("Normalized flat itinerary with converted dates:", {
      startDate: result.startDate,
      endDate: result.endDate,
      firstDayDate: result.days[0]?.date
    });
    
    return result;
  };

  // Handle URL parameters to automatically open print or wallet
  useEffect(() => {
    if (!loading && itinerary) {
      // Check for print parameter
      if (printParam === 'true') {
        // Remove the parameter from URL to prevent auto-printing on refresh
        navigate(`/itinerary/${id}`, { replace: true });
        // Short delay to ensure navigation completes
        setTimeout(() => {
          handlePrint();
        }, 300);
      }
      
      // Check for wallet parameter
      if (walletParam === 'true') {
        // Remove the parameter from URL
        navigate(`/itinerary/${id}`, { replace: true });
        // Short delay to ensure navigation completes
        setTimeout(() => {
          // setAppleWalletDialogOpen(true);
        }, 300);
      }
    }
  }, [loading, itinerary, printParam, walletParam, id, navigate]);
  
  const handleToggleFavorite = async () => {
    try {
      await toggleFavorite(id);
      
      // Update local state
      setItinerary(prev => ({
        ...prev,
        isFavorite: !prev.isFavorite
      }));
    } catch (err) {
      console.error('Error toggling favorite status:', err);
      setError('Failed to update favorite status. Please try again.');
    }
  };
  
  const handleDeleteItinerary = async () => {
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    try {
      await deleteItinerary(id);
      setDeleteDialogOpen(false);
      // Navigate back to saved trips page
      navigate('/saved-trips');
    } catch (err) {
      console.error('Error deleting itinerary:', err);
      setError('Failed to delete itinerary. Please try again.');
      setDeleteDialogOpen(false);
    }
  };
  
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleEditItinerary = () => {
    navigate(`/planner?edit=${id}`);
  };
  
  const handlePrint = () => {
    // Use the print functionality from the printUtils module
    printItinerary(itinerary, user);
  };
  
  const handleShareDialogOpen = async () => {
    setShareDialogOpen(true);
    // Automatically generate share link when dialog opens
    try {
      setShareLoading(true);
      
      // Create a share object with necessary data
      const shareData = {
        itineraryId: id,
        itineraryData: itinerary,
        expiresIn: '7d' // Fixed 7 days expiration
      };
      
      // Call API to create share link
      const result = await createShareLink(shareData);
      setShareLink(result.shareUrl);
    } catch (error) {
      console.error('Error generating share link:', error);
      setShareSnackbarMessage('Failed to generate share link. Please try again.');
      setShareSnackbarOpen(true);
    } finally {
      setShareLoading(false);
    }
  };
  
  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    // Reset share link state when dialog closes
    if (shareLink) {
      setShareLink('');
    }
  };
  
  const handleGenerateShareLink = async () => {
    if (!itinerary) return;
    
    try {
      setShareLoading(true);
      
      // Create a share object with necessary data - use the already normalized itinerary
      const shareData = {
        itineraryId: id,
        itineraryData: itinerary,
        expiresIn: shareLinkExpiry
      };
      
      // Call API to create share link
      const result = await createShareLink(shareData);
      setShareLink(result.shareUrl);
    } catch (error) {
      console.error('Error generating share link:', error);
      setShareSnackbarMessage('Failed to generate share link. Please try again.');
      setShareSnackbarOpen(true);
    } finally {
      setShareLoading(false);
    }
  };
  
  const handleCopyShareLink = () => {
    if (!shareLink) return;
    
    // Check if the Clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareLink)
        .then(() => {
          setShareSnackbarMessage('Link copied to clipboard!');
          setShareSnackbarOpen(true);
        })
        .catch(err => {
          console.error('Failed to copy link:', err);
          setShareSnackbarMessage('Failed to copy link.');
          setShareSnackbarOpen(true);
        });
    } else {
      // Fallback method for browsers that don't support the Clipboard API
      try {
        // Create a temporary textarea element
        const textArea = document.createElement('textarea');
        textArea.value = shareLink;
        
        // Make the textarea out of viewport
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        
        // Select and copy the text
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        
        // Remove the temporary element
        document.body.removeChild(textArea);
        
        if (successful) {
          setShareSnackbarMessage('Link copied to clipboard!');
        } else {
          setShareSnackbarMessage('Please copy the link manually.');
        }
        setShareSnackbarOpen(true);
      } catch (err) {
        console.error('Fallback copy method failed:', err);
        setShareSnackbarMessage('Please copy the link manually.');
        setShareSnackbarOpen(true);
      }
    }
  };
  
  const handleShareSocial = (platform) => {
    if (!shareLink) return;
    
    let shareUrl;
    const text = `Check out my travel itinerary for ${itinerary.destination}!`;
    
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareLink)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(text)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareLink)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };
  
  if (authLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '70vh' 
      }}>
        <CircularProgress size={isSmallScreen ? 36 : 40} />
        <Typography variant="body1" sx={{ mt: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '70vh' 
      }}>
        <CircularProgress size={isSmallScreen ? 36 : 40} />
        <Typography variant="body1" sx={{ mt: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Loading your itinerary...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, mt: { xs: 8, md: 10 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }
          }}
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />}
          onClick={() => navigate('/saved-trips')}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Back to My Trips
        </Button>
      </Container>
    );
  }
  
  if (!itinerary) {
    return (
      <Container maxWidth="md" sx={{ py: 4, mt: { xs: 8, md: 10 } }}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }
          }}
        >
          Itinerary not found or you don't have permission to view it.
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />}
          onClick={() => navigate('/saved-trips')}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Back to My Trips
        </Button>
      </Container>
    );
  }
  
  // Once we have the normalized itinerary data, render the ItineraryPage component
  // with our custom controls and dialogs
  return (
    <Box sx={{ mt: { xs: 8, md: 10 } }}>
      {/* Hero Section with Destination Image */}
      <Box
        sx={{
          position: 'relative', 
          height: { xs: '200px', md: '300px' },
          width: '100%',
          overflow: 'hidden',
          mb: 2
        }}
      >
        {/* Back button positioned at the top right of hero section */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 12, sm: 16 },
            right: { xs: 16, sm: 24, md: 32 },
            zIndex: 3
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '0.8rem', sm: '1.1rem' } }} />}
            onClick={() => navigate('/saved-trips')}
            size={isSmallScreen ? "small" : "medium"}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.85rem' },
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              boxShadow: theme.shadows[2],
              textTransform: 'none',
              fontWeight: 500,
              py: { xs: 0.5, sm: 0.75 },
              px: { xs: 1, sm: 2 }
            }}
          >
            Back to My Trips
          </Button>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${destinationImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)',
              zIndex: 1
            }
          }}
        />
        
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            zIndex: 2,
            pt: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}
        >
          {/* Mobile view: stacked layout with destination name and action icons */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            mt: { xs: 2 }
          }}>
            <Box sx={{ mb: { xs: 2 } }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  mb: 0.5,
                  fontSize: { xs: '1.75rem' }
                }}
              >
                {itinerary?.destination || 'Your Trip'}
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: { xs: '0.9rem' }
                }}
              >
                {safeFormatDate(itinerary.startDate, 'MMM d')} - {safeFormatDate(itinerary.endDate, 'MMM d, yyyy')}
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.75 },
              flexShrink: 0,
              alignSelf: 'flex-start'
            }}>
              <IconButton 
                onClick={handleToggleFavorite}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: { xs: '6px' }
                }}
                size="small"
              >
                {itinerary?.isFavorite ? 
                  <FavoriteIcon color="error" sx={{ fontSize: { xs: '1.1rem' } }} /> : 
                  <FavoriteBorderIcon sx={{ fontSize: { xs: '1.1rem' } }} />
                }
              </IconButton>
              
              <IconButton 
                onClick={handleShareDialogOpen}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: { xs: '6px' }
                }}
                size="small"
              >
                <IosShareIcon sx={{ fontSize: { xs: '1.1rem' } }} />
              </IconButton>
              
              <IconButton 
                onClick={handlePrint}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: { xs: '6px' }
                }}
                size="small"
              >
                <PrintIcon sx={{ fontSize: { xs: '1.1rem' } }} />
              </IconButton>
              
              <IconButton 
                onClick={handleDeleteItinerary}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: { xs: '6px' }
                }}
                size="small"
              >
                <DeleteIcon sx={{ fontSize: { xs: '1.1rem' } }} />
              </IconButton>
            </Box>
          </Box>

          {/* Desktop/Tablet view: Content stacked with icons below the date */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'block' }
          }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                color: 'white', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: { sm: '2.25rem', md: '2.75rem' }
              }}
            >
              {itinerary?.destination || 'Your Trip'}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'white', 
                opacity: 0.9,
                fontSize: { sm: '1.1rem', md: '1.25rem' },
                mb: 2
              }}
            >
              {safeFormatDate(itinerary.startDate, 'MMM d')} - {safeFormatDate(itinerary.endDate, 'MMM d, yyyy')}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              mt: 1
            }}>
              <IconButton 
                onClick={handleToggleFavorite}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: '8px'
                }}
              >
                {itinerary?.isFavorite ? 
                  <FavoriteIcon color="error" sx={{ fontSize: '1.25rem' }} /> : 
                  <FavoriteBorderIcon sx={{ fontSize: '1.25rem' }} />
                }
              </IconButton>
              
              <IconButton 
                onClick={handleShareDialogOpen}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: '8px'
                }}
              >
                <IosShareIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
              
              <IconButton 
                onClick={handlePrint}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: '8px'
                }}
              >
                <PrintIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
              
              <IconButton 
                onClick={handleDeleteItinerary}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
                  color: 'white',
                  padding: '8px'
                }}
              >
                <DeleteIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="lg" sx={{ pt: 0 }}>
        {/* Remove the Back button from here since we've moved it to the hero section */}
        
        {/* Use the actual ItineraryPage component in readonly mode */}
        <ItineraryPage 
          initialTripDetails={{
            destination: itinerary.destination,
            startDate: createLocalDate(itinerary.startDate),
            endDate: createLocalDate(itinerary.endDate),
            travelers: itinerary.travelers,
            adults: itinerary.adults || itinerary.travelers,
            children: itinerary.children || 0,
            budget: itinerary.budget,
            interests: Array.isArray(itinerary.interests) ? itinerary.interests : []
          }}
          itinerary={itinerary}
          isSharedView={false}
          isSavedView={true}
          itineraryId={id}
        />
      </Container>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{
          timeout: 300
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: { xs: 'flex-start', sm: 'flex-start' },
            pt: { xs: '5vh', sm: '10vh' }
          },
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 1 },
            m: { xs: 1, sm: 2 },
            width: '100%',
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease-in-out',
            animation: 'fadeIn 0.3s ease-out',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(20px) scale(0.98)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0) scale(1)'
              }
            }
          }
        }}
      >
        <DialogTitle 
          id="delete-dialog-title" 
          sx={{ 
            pb: 1,
            pt: { xs: 1.5, sm: 2.5 },
            px: { xs: 1.5, sm: 3 },
            fontSize: { xs: '0.95rem', sm: '1.1rem' },
            fontWeight: 600,
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0
          }}
        >
          Delete Itinerary
          <IconButton
            aria-label="close"
            onClick={cancelDelete}
            sx={{
              position: 'absolute',
              right: { xs: 4, sm: 8 },
              top: { xs: 4, sm: 8 },
              color: 'text.secondary',
              padding: { xs: 0.5, sm: 1 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          pb: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
        }}>
          <Alert 
            severity="warning" 
            icon={<DeleteIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1, sm: 2 },
              '& .MuiAlert-message': {
                fontSize: { xs: '0.75rem', sm: '0.85rem' }
              },
              '& .MuiAlert-icon': {
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                color: theme.palette.warning.main
              }
            }}
          >
            This action cannot be undone.
          </Alert>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              color: 'text.secondary',
              mb: 1
            }}
          >
            Are you sure you want to permanently delete your {itinerary?.destination} itinerary?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 1.5, sm: 3 }, 
          pb: { xs: 1.5, sm: 2.5 }, 
          pt: 0,
          gap: 1 
        }}>
          <Button 
            onClick={cancelDelete} 
            variant="outlined"
            size="small"
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 0.75 },
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 500,
              borderColor: alpha(theme.palette.text.secondary, 0.3),
              color: 'text.secondary',
              '&:hover': {
                borderColor: alpha(theme.palette.text.secondary, 0.5),
                backgroundColor: alpha(theme.palette.text.secondary, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            size="small"
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 0.75 },
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: alpha(theme.palette.error.main, 0.8)
              }
            }}
          >
            Delete Itinerary
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleShareDialogClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{
          timeout: 300
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: { xs: 'flex-start', sm: 'flex-start' },
            pt: { xs: '5vh', sm: '10vh' }
          },
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 1 },
            m: { xs: 1, sm: 2 },
            width: '100%',
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease-in-out',
            animation: 'fadeIn 0.3s ease-out',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(20px) scale(0.98)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0) scale(1)'
              }
            }
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            pt: { xs: 1.5, sm: 2.5 },
            px: { xs: 1.5, sm: 3 },
            fontSize: { xs: '0.95rem', sm: '1.1rem' },
            fontWeight: 600,
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0
          }}
        >
          Share Itinerary
          <IconButton
            aria-label="close"
            onClick={handleShareDialogClose}
            sx={{
              position: 'absolute',
              right: { xs: 4, sm: 8 },
              top: { xs: 4, sm: 8 },
              color: 'text.secondary',
              padding: { xs: 0.5, sm: 1 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          pb: { xs: 1.5, sm: 3 },
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          overflowY: 'auto',
          flex: 1
        }}>
          {shareLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '100px'
            }}>
              <CircularProgress size={24} />
            </Box>
          ) : shareLink ? (
            <>
              <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={shareLink}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      pr: 0.5,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.12)'
                      }
                    },
                    endAdornment: (
                      <Tooltip title="Copy link">
                        <IconButton 
                          edge="end" 
                          onClick={handleCopyShareLink}
                          size="small"
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { 
                              backgroundColor: 'rgba(94, 23, 235, 0.08)'
                            },
                            padding: { xs: 0.5, sm: 0.75 }
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }} />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Box>
              
              <Divider sx={{ 
                my: { xs: 1.5, sm: 2 },
                '&::before, &::after': {
                  borderColor: 'rgba(0, 0, 0, 0.08)'
                }
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    px: 1
                  }}
                >
                  Share via
                </Typography>
              </Divider>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 2, sm: 4 },
                mt: { xs: 1.5, sm: 2 }
              }}>
                <Tooltip title="Share on WhatsApp">
                  <IconButton 
                    onClick={() => handleShareSocial('whatsapp')} 
                    sx={{ 
                      color: '#25D366',
                      p: { xs: 0.75, sm: 1 },
                      '&:hover': { 
                        backgroundColor: 'rgba(37, 211, 102, 0.08)'
                      }
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share on Telegram">
                  <IconButton 
                    onClick={() => handleShareSocial('telegram')} 
                    sx={{ 
                      color: '#0088cc',
                      p: { xs: 0.75, sm: 1 },
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 136, 204, 0.08)'
                      }
                    }}
                  >
                    <TelegramIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share on Facebook">
                  <IconButton 
                    onClick={() => handleShareSocial('facebook')} 
                    sx={{ 
                      color: '#3b5998',
                      p: { xs: 0.75, sm: 1 },
                      '&:hover': { 
                        backgroundColor: 'rgba(59, 89, 152, 0.08)'
                      }
                    }}
                  >
                    <FacebookIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share on Twitter">
                  <IconButton 
                    onClick={() => handleShareSocial('twitter')} 
                    sx={{ 
                      color: '#1DA1F2',
                      p: { xs: 0.75, sm: 1 },
                      '&:hover': { 
                        backgroundColor: 'rgba(29, 161, 242, 0.08)'
                      }
                    }}
                  >
                    <TwitterIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <Alert 
              severity="error"
              sx={{
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1, sm: 2 },
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.75rem', sm: '0.85rem' }
                },
                '& .MuiAlert-icon': {
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }
              }}
            >
              Failed to generate share link. Please try again.
            </Alert>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for share notifications */}
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setShareSnackbarOpen(false)}
        message={shareSnackbarMessage}
        sx={{
          '& .MuiSnackbarContent-message': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }
        }}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setShareSnackbarOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default SavedItineraryPage; 